import React, { useState, useEffect } from 'react';

const TableArrangement = () => {
  const [seatingData, setSeatingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSeatingData();
  }, []);

  const fetchSeatingData = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/seating-chart/public');
      const data = await response.json();
      
      if (data.success) {
        setSeatingData(data.data);
      } else {
        setError(data.error || '获取座位信息失败');
      }
    } catch (err) {
      setError('获取座位信息失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // Create a map of position to table
  const getTableByPosition = (position) => {
    if (!seatingData) return null;
    return seatingData.tables.find(t => t.position === position);
  };

  // Render a single table card
  const renderTableCard = (position) => {
    const table = getTableByPosition(position);
    
    if (!table) {
      return (
        <div className="bg-white/50 rounded-lg p-4 border border-gray-200 min-h-[120px] flex items-center justify-center">
          <div className="text-center text-gray-400 text-sm">
            <div className="text-2xl mb-1">📍</div>
            <div>位置 {position}</div>
            <div>未安排</div>
          </div>
        </div>
      );
    }

    return (
      <div className="photo-overlay-soft rounded-lg p-4 border border-white/30 shadow-md hover:shadow-xl transition-shadow min-h-[120px]">
        <div className="text-center mb-2">
          <h4 className="text-lg font-chinese font-bold text-chinese-red">
            {table.table_number}
          </h4>
          {table.total_guests > 0 && (
            <div className="text-xs text-gray-600 mt-1">
              👥 {table.total_guests} 位宾客
            </div>
          )}
        </div>
        
        <div className="space-y-1">
          {table.guests.length === 0 ? (
            <div className="text-center text-gray-500 text-sm py-2">
              待分配 To Be Assigned
            </div>
          ) : (
            table.guests.map((guest, idx) => (
              <div key={idx} className="text-xl font-bold text-gray-900 text-center">
                {(guest.name || '').replace(/（[^）]*）/g, '').replace(/\([^)]*\)/g, '')}
                {guest.party_size > 1 && (
                  <span className="text-xs text-gray-500 ml-1">
                    ({guest.party_size}位)
                  </span>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-xl text-gray-600">加载座位信息中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-800">{error}</p>
        <button 
          onClick={fetchSeatingData}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          重试 Retry
        </button>
      </div>
    );
  }

  if (!seatingData || seatingData.tables.length === 0) {
    return (
      <div className="text-center py-12 text-gray-600">
        <div className="text-4xl mb-4">🪑</div>
        <p className="text-lg">座位安排即将公布</p>
        <p className="text-sm mt-2">Seating arrangements coming soon</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Desktop/Tablet Grid Layout */}
      <div className="hidden md:grid md:grid-cols-4 gap-4">
        {/* Row 1 */}
        <div className="col-start-2">{renderTableCard(1)}</div>
        <div className="col-start-3">{renderTableCard(2)}</div>
        
        {/* Row 2 */}
        <div className="col-start-1">{renderTableCard(5)}</div>
        <div className="col-start-4">{renderTableCard(6)}</div>
        
        {/* Row 3 */}
        <div className="col-start-2">{renderTableCard(3)}</div>
        <div className="col-start-3">{renderTableCard(4)}</div>
        
        {/* Row 4 */}
        <div className="col-start-1">{renderTableCard(9)}</div>
        <div className="col-start-4">{renderTableCard(10)}</div>
        
        {/* Row 5 */}
        <div className="col-start-2">{renderTableCard(7)}</div>
        <div className="col-start-3">{renderTableCard(8)}</div>
        
        {/* Row 6 */}
        <div className="col-start-1">{renderTableCard(11)}</div>
        <div className="col-start-4">{renderTableCard(12)}</div>
      </div>

      {/* Mobile Layout - Stacked */}
      <div className="md:hidden space-y-4">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(position => {
          const table = getTableByPosition(position);
          if (!table) return null;
          return (
            <div key={position}>
              {renderTableCard(position)}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-8 text-center text-sm text-gray-600">
        <p>💡 提示：以上为婚礼现场座位安排</p>
        <p className="text-xs mt-1">Tip: Above is the wedding venue seating arrangement</p>
      </div>
    </div>
  );
};

export default TableArrangement;

